/**
 * 日本の郵便番号から住所を Zipcloud API で取得する再利用可能モジュール。
 * 画面固有の DOM 更新は bindJapanesePostalCodeLookup のコールバックに委ねる。
 */

const DEFAULT_REQUEST_TIMEOUT_MS = 15000;
const UNICODE_HYPHENS_PATTERN = /[\uFF0D\u2212\u2010-\u2015\u30FC\uFE58\uFE63\uFF70]/g;
const FULLWIDTH_DIGIT_BASE = 0xff10;

/**
 * Zipcloud 検索 URL を解決する。
 * 優先順: window.jpenergyZipcloudSearchUrl → meta[name=jpenergy-zipcloud-search-url]
 *
 * @returns {string|null}
 */
function resolveZipcloudSearchUrl() {
    const fromWindow = window.jpenergyZipcloudSearchUrl;
    if (typeof fromWindow === 'string' && fromWindow !== '') {
        return fromWindow;
    }

    const meta = document.querySelector('meta[name="jpenergy-zipcloud-search-url"]');
    if (meta instanceof HTMLMetaElement && meta.content !== '') {
        return meta.content;
    }

    return null;
}

/**
 * 画面上の入力値は変更せず、API 呼び出し用に郵便番号を 7 桁へ正規化する。
 *
 * @param {string} rawPostalCode
 * @returns {string|null}
 */
function normalizeJapanesePostalCodeForLookup(rawPostalCode) {
    if (typeof rawPostalCode !== 'string') {
        return null;
    }

    let normalized = rawPostalCode.trim();
    normalized = normalized.replace(/[\uFF10-\uFF19]/g, (character) => {
        return String.fromCharCode(character.charCodeAt(0) - FULLWIDTH_DIGIT_BASE + 0x30);
    });
    normalized = normalized.replace(UNICODE_HYPHENS_PATTERN, '-');
    normalized = normalized.replace(/-/g, '');

    if (!/^\d{7}$/.test(normalized)) {
        return null;
    }

    return normalized;
}

/**
 * @param {Record<string, unknown>|null|undefined} resultRow
 * @returns {JapanesePostalCodeLookupResult|null}
 */
function parseZipcloudResultRow(resultRow) {
    if (resultRow === null || resultRow === undefined || typeof resultRow !== 'object') {
        return null;
    }

    const prefectureName = typeof resultRow.address1 === 'string' ? resultRow.address1 : '';
    const city = typeof resultRow.address2 === 'string' ? resultRow.address2 : '';
    const town = typeof resultRow.address3 === 'string' ? resultRow.address3 : '';
    const prefcode = typeof resultRow.prefcode === 'string' ? resultRow.prefcode : '';
    const addressWithoutPrefecture = `${city}${town}`;

    return {
        prefcode,
        prefectureName,
        city,
        town,
        addressWithoutPrefecture,
        fullAddress: `${prefectureName}${addressWithoutPrefecture}`,
    };
}

/**
 * @typedef {Object} JapanesePostalCodeLookupResult
 * @property {string} prefcode
 * @property {string} prefectureName
 * @property {string} city
 * @property {string} town
 * @property {string} addressWithoutPrefecture
 * @property {string} fullAddress
 */

/**
 * @typedef {Object} LookupJapanesePostalCodeOptions
 * @property {string} [apiSearchUrl]
 * @property {boolean} [showWaitModal]
 * @property {number} [timeoutMs]
 */

/**
 * @param {string} postalCodeDigits
 * @param {LookupJapanesePostalCodeOptions} [options]
 * @returns {Promise<JapanesePostalCodeLookupResult|null>}
 */
async function lookupJapanesePostalCode(postalCodeDigits, options = {}) {
    const apiSearchUrl = options.apiSearchUrl ?? resolveZipcloudSearchUrl();
    if (apiSearchUrl === null || apiSearchUrl === '') {
        throw new Error('Zipcloud search URL is not configured');
    }

    const showWaitModal = options.showWaitModal !== false;
    const timeoutMs = typeof options.timeoutMs === 'number' ? options.timeoutMs : DEFAULT_REQUEST_TIMEOUT_MS;
    const requestUrl = `${apiSearchUrl.replace(/\/$/, '')}?zipcode=${encodeURIComponent(postalCodeDigits)}`;

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(() => {
        abortController.abort();
    }, timeoutMs);

    if (showWaitModal && typeof window.jpenergyShowAjaxWaitModal === 'function') {
        window.jpenergyShowAjaxWaitModal();
    }

    try {
        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
            signal: abortController.signal,
        });

        if (!response.ok) {
            throw new Error(`Zipcloud HTTP ${response.status}`);
        }

        const body = await response.json();
        const responseStatus = Number(body?.status);
        if (responseStatus !== 200) {
            throw new Error(
                typeof body?.message === 'string' && body.message !== ''
                    ? body.message
                    : `Zipcloud status ${responseStatus}`,
            );
        }

        const results = Array.isArray(body?.results) ? body.results : [];
        if (results.length === 0) {
            return null;
        }

        return parseZipcloudResultRow(results[0]);
    } finally {
        window.clearTimeout(timeoutId);
        if (showWaitModal && typeof window.jpenergyHideAjaxWaitModal === 'function') {
            window.jpenergyHideAjaxWaitModal();
        }
    }
}

/**
 * @typedef {Object} JapanesePostalCodeLookupHandlerOptions
 * @property {HTMLInputElement} postalCodeInput
 * @property {(result: JapanesePostalCodeLookupResult) => void} [onResolved]
 * @property {() => void} [onNotFound]
 * @property {() => void} [onInvalidFormat]
 * @property {(error: Error) => void} [onRequestError]
 * @property {LookupJapanesePostalCodeOptions} [lookupOptions]
 */

/**
 * @param {JapanesePostalCodeLookupHandlerOptions} options
 */
function runJapanesePostalCodeLookupFromInput(options) {
    const postalCodeInput = options.postalCodeInput;
    if (!(postalCodeInput instanceof HTMLInputElement)) {
        return;
    }

    const normalizedPostalCode = normalizeJapanesePostalCodeForLookup(postalCodeInput.value);
    if (normalizedPostalCode === null) {
        if (typeof options.onInvalidFormat === 'function') {
            options.onInvalidFormat();
        }

        return;
    }

    lookupJapanesePostalCode(normalizedPostalCode, options.lookupOptions)
        .then((lookupResult) => {
            if (lookupResult === null) {
                if (typeof options.onNotFound === 'function') {
                    options.onNotFound();
                }

                return;
            }

            if (typeof options.onResolved === 'function') {
                options.onResolved(lookupResult);
            }
        })
        .catch((error) => {
            if (typeof options.onRequestError === 'function') {
                options.onRequestError(error instanceof Error ? error : new Error(String(error)));
            }
        });
}

/**
 * @typedef {Object} BindJapanesePostalCodeLookupOptions
 * @property {HTMLInputElement|string} postalCodeInput
 * @property {HTMLButtonElement|string} [searchButton]
 * @property {string} [triggerEvent]
 * @property {(result: JapanesePostalCodeLookupResult) => void} [onResolved]
 * @property {() => void} [onNotFound]
 * @property {() => void} [onInvalidFormat]
 * @property {(error: Error) => void} [onRequestError]
 * @property {LookupJapanesePostalCodeOptions} [lookupOptions]
 */

/**
 * @param {BindJapanesePostalCodeLookupOptions} options
 */
function bindJapanesePostalCodeLookup(options) {
    const postalCodeInput =
        typeof options.postalCodeInput === 'string'
            ? document.querySelector(options.postalCodeInput)
            : options.postalCodeInput;

    if (!(postalCodeInput instanceof HTMLInputElement)) {
        return;
    }

    const handlerOptions = {
        postalCodeInput,
        onResolved: options.onResolved,
        onNotFound: options.onNotFound,
        onInvalidFormat: options.onInvalidFormat,
        onRequestError: options.onRequestError,
        lookupOptions: options.lookupOptions,
    };

    const runLookup = () => {
        runJapanesePostalCodeLookupFromInput(handlerOptions);
    };

    const searchButton =
        options.searchButton === undefined
            ? null
            : typeof options.searchButton === 'string'
              ? document.querySelector(options.searchButton)
              : options.searchButton;

    if (searchButton instanceof HTMLButtonElement) {
        searchButton.addEventListener('click', runLookup);

        return;
    }

    const triggerEvent = options.triggerEvent ?? 'change';
    postalCodeInput.addEventListener(triggerEvent, runLookup);
}

window.jpenergyResolveZipcloudSearchUrl = resolveZipcloudSearchUrl;
window.jpenergyNormalizeJapanesePostalCodeForLookup = normalizeJapanesePostalCodeForLookup;
window.jpenergyLookupJapanesePostalCode = lookupJapanesePostalCode;
window.jpenergyRunJapanesePostalCodeLookupFromInput = runJapanesePostalCodeLookupFromInput;
window.jpenergyBindJapanesePostalCodeLookup = bindJapanesePostalCodeLookup;

export {
    bindJapanesePostalCodeLookup,
    lookupJapanesePostalCode,
    normalizeJapanesePostalCodeForLookup,
    resolveZipcloudSearchUrl,
    runJapanesePostalCodeLookupFromInput,
};
